// src/components/Users.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./Users.css";

import UserCard from "./UserCard";
import EditUserModal from "./modals/EditUserModal";
import CreateUserModal from "./modals/CreateUserModal";
import ChangePasswordModal from "./modals/ChangePasswordModal";
import ChangePolicyModal from "./modals/ChangePolicyModal";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";

import { useNotification } from "../NotificationContext";

const API_BASE = import.meta.env.VITE_BACKEND_URL;

export default function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Estados para modais
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const { addNotification } = useNotification();

  useEffect(() => {
    fetchUsers();
  }, []);

  // filtra de forma segura, sem erros de undefined
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    setFilteredUsers(
      users.filter((u) => {
        const full =
          `${u.firstName || ""} ${u.lastName || ""} ${u.username || ""}`.toLowerCase();
        return full.includes(term);
      })
    );
  }, [searchTerm, users]);

  async function fetchUsers() {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar usuários.");
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(user) {
    setUserToEdit(user);
    setShowEditModal(true);
  }

  function handleOpenPassword(user) {
    setSelectedUser(user);
    setShowPasswordModal(true);
  }

  function handleOpenPolicy(user) {
    setSelectedUser(user);
    setShowPolicyModal(true);
  }

  function handleDeleteClick(user) {
    setUserToDelete(user);
    setShowConfirmDelete(true);
  }

  async function confirmDelete() {
    try {
      await axios.delete(
        `${API_BASE}/api/users/${encodeURIComponent(userToDelete.email)}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      addNotification({
        message: "Usuário excluído com sucesso!",
        type: "success",
        delay: 3000,
      });
      fetchUsers();
    } catch (err) {
      console.error(err);
      addNotification({
        message: "Erro ao excluir usuário.",
        type: "error",
        delay: 3000,
      });
    } finally {
      setShowConfirmDelete(false);
      setUserToDelete(null);
    }
  }

  async function handleCreate({ email, firstName, lastName, password, policy, role }) {
    const payload = { email, firstName, lastName, password, policy, role };
    try {
      console.log("Criando sub-usuário com payload:", payload);
      await axios.post(
        `${API_BASE}/api/users`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      addNotification({ message: "Usuário criado com sucesso!", type: "success", delay: 3000 });
      fetchUsers();
      return { ok: true };
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || "Erro ao criar usuário.";
      addNotification({
        message,
        type: "error",
        delay: 3000,
      });
      return { ok: false, error: message };
    }
  }
  function handleEditSave(updated) {
    addNotification({
      message: "Usuário editado com sucesso!",
      type: "success",
      delay: 3000,
    });
    setShowEditModal(false);
    fetchUsers();
  }

  return (
    <div className="users-page container-page">
      <div className="users-header d-flex justify-content-between align-items-center mb-3">
        <h2>Usuários</h2>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Criar Usuário
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar por nome ou e-mail..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading && <p>Carregando usuários...</p>}
      {error && <p className="text-danger">{error}</p>}

      {!loading && !error && (
        <div className="user-list">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <UserCard
                key={user.username}
                user={user}
                onEdit={handleEdit}
                onChangePassword={handleOpenPassword}
                onChangePolicy={handleOpenPolicy}
                onDelete={handleDeleteClick}
              />
            ))
          ) : (
            <p>Nenhum usuário encontrado.</p>
          )}
        </div>
      )}

      {/* Modais */}
      <EditUserModal
        show={showEditModal}
        user={userToEdit}
        onClose={() => setShowEditModal(false)}
        onSaved={handleEditSave}
      />
      <CreateUserModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
      />
      <ChangePasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        username={selectedUser?.username}
      />
      <ChangePolicyModal
        show={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        username={selectedUser?.username}
        onSubmit={fetchUsers}
      />
      <ConfirmDeleteModal
        show={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        message={`Deseja realmente excluir o usuário "${userToDelete?.email}"?`}
      />
    </div>
  );
}
